<script setup lang="ts">
import {computed, useSlots} from 'vue'
import {CypressUniqueNameType} from '@toolify/client/cypress/types/CypressUniqueNameType'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'

interface Props {
  isSelected: boolean
  itemHeight?: number
  type?: 'mobile'
  padding?: [number, number, number, number]
  hideSelectedIcon?: boolean
  isRenaming?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: null,
  padding: null,
  hideSelectedIcon: false,
  isRenaming: false,
})
const slots = useSlots()

const additionalStyles = computed(() => {
  const hasLeft = Boolean(slots['left'])
  const columns = [
    hasLeft ? 'var(--size-sm)' : null,
    'minmax(0, 1fr)',
    slots['select'] || !props.hideSelectedIcon ? 'auto' : null,
    slots['right'] ? 'auto' : null,
  ].filter(Boolean)

  return {
    gridTemplateColumns: columns.join(' '),
    padding: props.padding ? props.padding.map(p => `${p}px`).join(' ') : null,
    '--first-gap-adjust': hasLeft ? 'calc(var(--space-1) - var(--space-2))' : '0',
  }
})

const itemHeight = computed(() => {
  if (!props.itemHeight) return 'auto'
  return `${props.itemHeight}px`
})
</script>

<template>
  <div
    :class="[
      'item-container',
      { 'is-selected': props.isSelected },
      { 'is-renaming': props.isRenaming },
      props.type ? `type-${props.type}` : ''
    ]"
    :style="additionalStyles"
    :data-cy="CypressUniqueNameType.ChooseItemComponent"
  >
    <div class="left-wrapper" v-if="$slots.left">
      <slot name="left"/>
    </div>

    <div
      class="default-wrapper"
      :class="{
        'with-left-icon': $slots.left,
        'mobile-selected-text': props.type === 'mobile' && props.isSelected,
      }"
    >
      <slot name="default"/>
    </div>

    <div class="select-wrapper" v-if="($slots.select || !props.hideSelectedIcon) && !props.isRenaming">
      <slot name="select" v-if="$slots.select"/>
      <IconComponent v-else :name="props.isSelected ? IconNameType.Check : null" class="select-icon"/>
    </div>

    <div class="right-wrapper" v-if="$slots.right && !props.isRenaming">
      <slot name="right"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.item-container {
  display: grid;
  gap: map-get(variables.$spacing, space-2);
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding: map-get(variables.$spacing, space-1) map-get(variables.$spacing, space-2);
  border-radius: map-get(variables.$componentRadius, standard);
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: v-bind(itemHeight);

  &.type-mobile.is-selected .default-wrapper {
    color: map-get(variables.$colors, primary);
    font-weight: 500;
  }

  & .with-left-icon {
    padding-left: 0;
  }

  & > .default-wrapper {
    margin-left: var(--first-gap-adjust);
  }
}

.default-wrapper {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: 100%;
  display: flex;
  align-items: center;
}

.left-wrapper {
  display: grid;
  align-items: center;
  justify-items: center;
  min-width: 0;
}

.select-wrapper {
  min-width: map-get(variables.$iconSizes, medium);
  display: flex;
  justify-content: center;
}

.select-icon {
  color: map-get(variables.$colors, content-dim);

  :deep(.icon-content) {
    color: map-get(variables.$colors, content-dim);
  }
}

.is-renaming {
  height: map-get(variables.$sizes, size-lg);
  border-radius: map-get(variables.$radiuses, radius-sm) !important;
  border: 1px solid map-get(variables.$colors, container-high);
  background: map-get(variables.$colors, surface-dim) !important;
  padding-right: 0 !important;

  .default-wrapper {
    padding: 0;
  }
}

.right-wrapper {
  display: grid;
  align-items: center;
}
</style>
